// Stránkování dlouhých seznamů.
// (Krok B1b: rozděleno ze src/app.js beze změny kódu; importy/exporty doplněny automaticky.)
import { DELETED_RETENTION_DAYS, canManageContent, cascadeDeleteAudits, matchesCurrentCompany, queueWrite, state } from './state.js';
import { db } from './boot.js';
import { daysSince } from '../views/dashboard.js';
import { render } from '../main-render.js';
import { isReadOnly } from './licence.js';

// ---------- stránkování (sdílené pro dlouhé seznamy) ----------
// Jedna volba velikosti stránky platí pro všechny stránkované seznamy v
// appce najednou (Historie, Opatření, Plán auditů, Audit→Rozpracované/
// Naplánované, Uživatelé a role) — uložená v prohlížeči, přežije reload i
// jiný seznam. Jako appka jinde loguje k obdobě, je i tohle jen *renderovací*
// omezení: data se dál načítají celá (potřebuje je třeba Dashboard), jen se
// z nich vykreslí jen zvolená stránka.
var PAGE_SIZE_OPTIONS = [10, 25, 50, 100, 'all'];
var PAGE_SIZE_PREF_KEY = 'audit_app_page_size_v1';
function loadPageSizePref(){
  try{
    var raw = localStorage.getItem(PAGE_SIZE_PREF_KEY);
    if(raw==='all') return 'all';
    var n = parseInt(raw,10);
    if(PAGE_SIZE_OPTIONS.indexOf(n)!==-1) return n;
  }catch(e){ /* storage unavailable (private mode apod.) — použije se výchozí */ }
  return 10;
}
function savePageSizePref(v){
  try{ localStorage.setItem(PAGE_SIZE_PREF_KEY, String(v)); }catch(e){}
}
var PAGE_KEYS = ['historyPage','correctivePage','schedulePage','auditOpenPage','auditStartablePage','usersPage',
  'companiesPage','branchesPage','rpPage','auditTypesPage','scheduleManagePage','auditorGroupsPage','trashPage','corrHistPage','bulkInvitePage','accessPage','historyTablePage','correctiveTablePage','wpAuditsPage'];
window.App = window.App || {};
App.setPageSize = function(v){
  state.pageSize = (v==='all') ? 'all' : (parseInt(v,10)||10);
  savePageSizePref(state.pageSize);
  PAGE_KEYS.forEach(function(k){ state[k] = 1; });
  Object.keys(state).forEach(function(k){ if(/^wpPage_/.test(k)) state[k] = 1; }); // pracoviště: stránkování po provozovnách
  render();
};
App.setPage = function(pageKey, page){
  state[pageKey] = Math.max(1, page|0);
  render();
};
// Vrátí jen tu "stránku" ze seznamu, podle aktuálně zvolené velikosti
// stránky a uloženého čísla stránky pro dané místo (pageKey). Číslo
// stránky se ohraničí na platný rozsah (seznam mohl mezitím zkrátit). Když
// je dáno sigKey/sig (otisk aktivního filtru/vyhledávání/řazení) a ten se
// od minula změnil, stránkování se vrátí na stránku 1 — jinak by po změně
// filtru uživatel zůstal na "stránce 3", která už znamená něco jiného.
function paginateList(list, pageKey, sigKey, sig){
  if(sigKey && state[sigKey]!==sig){ state[sigKey]=sig; state[pageKey]=1; }
  var size = state.pageSize;
  var totalPages = (size==='all') ? 1 : Math.max(1, Math.ceil(list.length/size));
  var page = Math.min(Math.max(1, state[pageKey]||1), totalPages);
  state[pageKey] = page;
  var items = (size==='all') ? list : list.slice((page-1)*size, page*size);
  return {items:items, page:page, totalPages:totalPages, total:list.length};
}
// Pořadí první položky stránky v celém seznamu (pro šipky Nahoru/Dolů).
function pageOffset(info){ return state.pageSize==='all' ? 0 : (info.page-1)*state.pageSize; }
function paginationControlsHtml(pageKey, info){
  // Do 10 položek (nejmenší velikost stránky) se celé stránkování skrývá —
  // volba "Na stránku" by nic nezměnila. Při jediné stránce se skrývá
  // i Předchozí/Další a "Strana 1 z 1".
  if(info.total<=PAGE_SIZE_OPTIONS[0]) return '';
  var showPager = info.totalPages>1;
  var html = '<div style="display:flex;align-items:center;justify-content:'+(showPager?'space-between':'flex-end')+';gap:10px;flex-wrap:wrap;margin-top:12px">';
  if(showPager){
    html += '<div style="display:flex;align-items:center;gap:6px">' +
      '<button type="button" class="btn btn-sm btn-ghost" '+(info.page<=1?'disabled':'')+' onclick="App.setPage(\''+pageKey+'\', '+(info.page-1)+')">‹ Předchozí</button>' +
      '<span class="muted" style="font-size:var(--fs-sm);white-space:nowrap">Strana '+info.page+' z '+info.totalPages+' ('+info.total+' položek)</span>' +
      '<button type="button" class="btn btn-sm btn-ghost" '+(info.page>=info.totalPages?'disabled':'')+' onclick="App.setPage(\''+pageKey+'\', '+(info.page+1)+')">Další ›</button>' +
      '</div>';
  }
  html += '<div style="display:flex;align-items:center;gap:6px">' +
    '<span class="muted" style="font-size:var(--fs-sm);white-space:nowrap">Na stránku:</span>' +
    '<select class="input-sm" onchange="App.setPageSize(this.value)">' +
    PAGE_SIZE_OPTIONS.map(function(o){
      var val = o==='all' ? 'all' : o;
      return '<option value="'+val+'" '+(state.pageSize===val?'selected':'')+'>'+(o==='all'?'Vše':o)+'</option>';
    }).join('') +
    '</select></div>' +
    '</div>';
  return html;
}
function deletedAudits(){
  return Array.from(state.audits.entries()).filter(function(e){ return !!e[1].deletedAt && matchesCurrentCompany(e[1]); })
    .sort(function(a,b){ return new Date(b[1].deletedAt)-new Date(a[1].deletedAt); });
}
var purgeAttempted = new Set();
function maybePurgeOldDeletedAudits(){
  if(!db || !canManageContent() || isReadOnly()) return;
  state.audits.forEach(function(a, id){
    if(!a.deletedAt || purgeAttempted.has(id)) return;
    var ageDays = daysSince(a.deletedAt);
    if(ageDays===null || ageDays < DELETED_RETENTION_DAYS) return;
    purgeAttempted.add(id);
    hardDeleteAudit(id);
  });
}
function hardDeleteAudit(id, precollected){
  if(!db) return;
  queueWrite(function(){ return cascadeDeleteAudits([id], precollected); });
}

export {
  deletedAudits,
  hardDeleteAudit,
  loadPageSizePref,
  maybePurgeOldDeletedAudits,
  pageOffset,
  paginateList,
  paginationControlsHtml
};
